import React, { useEffect, useRef, useState } from 'react';
import {
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronDown
} from 'react-icons/fa';

/**
 * App-styled pagination: << < page-dropdown > >>
 * Matches secondary (#00AFEF) button language used across panels.
 */
const PaginationBar = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  className = ''
}) => {
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / itemsPerPage) || 1);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [currentPage]);

  const go = (page) => {
    const next = Math.min(totalPages, Math.max(1, page));
    if (next !== currentPage) onPageChange(next);
    setOpen(false);
  };

  const atFirst = currentPage <= 1;
  const atLast = currentPage >= totalPages || totalItems === 0;

  const btnBase =
    'flex justify-center items-center w-10 h-10 rounded-md transition-colors disabled:cursor-not-allowed';
  const btnActive = 'bg-secondary text-white hover:opacity-90';
  const btnDisabled = 'bg-gray-500 text-white opacity-70';

  return (
    <div className={`flex justify-end items-center py-4 gap-2 sm:gap-3 flex-wrap ${className}`}>
      <button
        type="button"
        title="First page"
        disabled={atFirst}
        onClick={() => go(1)}
        className={`${btnBase} ${atFirst ? btnDisabled : btnActive}`}
      >
        <FaAngleDoubleLeft size={16} />
      </button>
      <button
        type="button"
        title="Previous page"
        disabled={atFirst}
        onClick={() => go(currentPage - 1)}
        className={`${btnBase} ${atFirst ? btnDisabled : btnActive}`}
      >
        <FaAngleLeft size={18} />
      </button>

      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-10 min-w-[7.5rem] px-3 rounded-md border border-secondary bg-white text-primary font-semibold flex items-center justify-between gap-2 hover:bg-sky-50 transition-colors"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>
            {currentPage} <span className="text-darkGray font-medium">/ {totalPages}</span>
          </span>
          <FaChevronDown
            size={12}
            className={`text-secondary transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {open && (
          <div className="absolute bottom-full mb-2 right-0 z-40 w-56 rounded-xl border border-secondary/30 bg-white shadow-lg overflow-hidden">
            <div className="px-3 py-2 bg-secondary text-white text-xs font-semibold tracking-wide">
              Jump to page
            </div>
            <div
              className="p-2 grid grid-cols-5 gap-1.5 max-h-52 overflow-y-auto"
              role="listbox"
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                const active = pageNum === currentPage;
                return (
                  <button
                    key={pageNum}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => go(pageNum)}
                    className={`h-9 rounded-md text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-secondary text-white shadow-sm'
                        : 'bg-gray-50 text-primary hover:bg-secondary/10 hover:text-secondary'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        title="Next page"
        disabled={atLast}
        onClick={() => go(currentPage + 1)}
        className={`${btnBase} ${atLast ? btnDisabled : btnActive}`}
      >
        <FaAngleRight size={18} />
      </button>
      <button
        type="button"
        title="Last page"
        disabled={atLast}
        onClick={() => go(totalPages)}
        className={`${btnBase} ${atLast ? btnDisabled : btnActive}`}
      >
        <FaAngleDoubleRight size={16} />
      </button>
    </div>
  );
};

export default PaginationBar;
